import React, { useState } from "react";
import { Typography, Box, CircularProgress, Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import * as selectors from "selectors";
import { AvailableHoursSelector } from "../ClientAvailability/AvailableHoursSelector";
import { PreferredAvailabilityCard } from "../ClientAvailability/PreferredAvailabilityCard";
import { useClientAvailability, useUpdateClientAvailability } from "../ClientAvailability/hooks";
import { useGlobalToast } from "components/GlobalToastProvider";
import { useCustomerDetails } from "hooks/useCustomerDetails";
import camelCaseToTitleCase from "utils/camelCaseToTitleCase";
import { formatTimeZoneToAbbreviation } from "utils/formatTimeZoneToAbbreviation";
import SectionWrapper from "../../Widgets/SectionWrapper";

const daysOrder = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
] as const;

const serviceTypesOrder = ["directTherapy", "caregiverTraining"] as const;

type DayOfWeek = typeof daysOrder[number];
type ServiceType = typeof serviceTypesOrder[number];

interface DayAvailability {
  isActive: boolean;
  ranges: Array<{
    start: string;
    end: string;
    preferred?: boolean;
  }>;
}

interface ServiceAvailability {
  serviceType: ServiceType;
  availability: Record<DayOfWeek, DayAvailability>;
}

interface AvailableHours {
  services: ServiceAvailability[];
}

interface ClientBasicInfo {
  client_id?: string;
  timezone_jhi?: string;
  timezone?: string;
  name?: string;
}

const serviceTypesDisplayNames: Record<ServiceType, string> = {
  directTherapy: "Client Availability",
  caregiverTraining: "Caregiver Availability",
};

const initializeDefaultHours = (): Record<DayOfWeek, DayAvailability> => {
  return daysOrder.reduce((acc, day) => {
    acc[day] = {
      isActive: false,
      ranges: [],
    };
    return acc;
  }, {} as Record<DayOfWeek, DayAvailability>);
};

const defaultServiceTypes: AvailableHours = {
  services: serviceTypesOrder.map((type) => ({
    serviceType: type,
    availability: initializeDefaultHours(),
  })),
};

/**
 * Standalone WeeklyAvailability component that manages its own state and data fetching.
 * This component handles the weekly availability section independently with its own edit mode.
 * Extracts clientId from URL parameters - no props required.
 */
const WeeklyAvailability: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const { showToast } = useGlobalToast();
  const isCaregiver = useSelector(selectors.isCaregiver);
  const userPermissions = useSelector(selectors.getUserPermissionsList);
  const canEdit = !isCaregiver && Boolean(userPermissions?.edit_client_availability);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [, setIsSaving] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [availableHours, setAvailableHours] = useState<AvailableHours>(defaultServiceTypes);
  const [originalAvailableHours, setOriginalAvailableHours] = useState<AvailableHours | null>(null);

  // Fetch client basic info for timezone
  const { data: clientBasicInfo, isLoading: isLoadingClientInfo } = useCustomerDetails(
    clientId
  ) as { data: ClientBasicInfo | undefined; isLoading: boolean };

  const { isLoading, refetch } = useClientAvailability({
    clientId,
    enabled: !!clientId,
    onSuccess: ({ data }) => {
      if (data?.services?.length > 0) {
        setAvailableHours(data);
        setOriginalAvailableHours(data);
      } else {
        setAvailableHours(defaultServiceTypes);
        setOriginalAvailableHours(defaultServiceTypes);
      }
    },
  });

  const { mutate: saveAvailableHours } = useUpdateClientAvailability({
    onSuccess: () => {
      setIsSaving(false);
      setHasChanges(false);
      setEditMode(false);
      refetch();
      showToast({
        message: "Weekly availability updated successfully!",
        errorState: false,
      });
    },
    onError: () => {
      setIsSaving(false);
      showToast({
        message: "Whoops! Something went wrong",
        errorState: true,
      });
    },
  });

  const updateDayData = ({
    serviceType,
    day,
    isActive,
    timeRanges,
  }: {
    serviceType: ServiceType;
    day: DayOfWeek;
    isActive: boolean;
    timeRanges: Array<{ start: string; end: string; preferred?: boolean }>;
  }): void => {
    const timeRangesArray = Array.isArray(timeRanges) ? timeRanges : [];
    const updatedServices = availableHours.services.map((service) => {
      if (service.serviceType === serviceType) {
        return {
          ...service,
          availability: {
            ...service.availability,
            [day]: { isActive, ranges: timeRangesArray },
          },
        };
      }
      return service;
    });
    setAvailableHours({ services: updatedServices });
    setHasChanges(true);
  };

  const copyToDays = ({
    serviceType,
    day,
    selectedDays,
  }: {
    serviceType: ServiceType;
    day: DayOfWeek;
    selectedDays: DayOfWeek[];
  }): void => {
    const service = availableHours.services.find((s) => s.serviceType === serviceType);
    if (!service) return;

    const copiedDayData = service.availability[day];
    const updatedAvailability = selectedDays.reduce(
      (acc, selectedDay) => {
        acc[selectedDay] = copiedDayData;
        return acc;
      },
      { ...service.availability }
    );

    const updatedServices = availableHours.services.map((s) => {
      if (s.serviceType === serviceType) {
        return { ...s, availability: updatedAvailability };
      }
      return s;
    });

    setAvailableHours({ services: updatedServices });
    setHasChanges(true);
  };

  const handleEdit = (): void => {
    setEditMode(true);
    setOriginalAvailableHours(availableHours);
  };

  const handleCancel = (): void => {
    if (originalAvailableHours) {
      setAvailableHours(originalAvailableHours);
    }
    setEditMode(false);
    setHasChanges(false);
  };

  const handleSave = (): void => {
    if (!clientId) {
      showToast({
        message: "Client ID is missing",
        errorState: true,
      });
      return;
    }

    setIsSaving(true);
    saveAvailableHours({ newAvailableHours: availableHours, clientId });
  };

  // If no clientId, show error
  if (!clientId) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="error">
          Client ID is required
        </Typography>
      </Box>
    );
  }

  // Loading state
  if (isLoadingClientInfo) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const basicInfoForComponent: ClientBasicInfo = clientBasicInfo ?? { client_id: clientId };
  const timezone =
    basicInfoForComponent.timezone_jhi || basicInfoForComponent.timezone || "America/New_York";

  return (
    <SectionWrapper
      title="Weekly Availability"
      description="To set the client's weekly availability for therapy sessions, please indicate the days and times they are available. Times can also be identified as preferred therapy hours for best fit scheduling efforts."
      editMode={editMode}
      onEdit={canEdit ? handleEdit : undefined}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={!hasChanges}
    >
      <Stack spacing={7}>
        {availableHours?.services && (
          <PreferredAvailabilityCard
            serviceTypesOrder={serviceTypesOrder}
            availableHours={availableHours}
            basicInfo={basicInfoForComponent}
            clientTimeZone={timezone}
          />
        )}
        {serviceTypesOrder.map((serviceType, index) => (
          <Box key={index}>
            <Typography
              variant="inputLabel"
              color={(theme) => theme.palette.text.secondary}
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              {serviceTypesDisplayNames[serviceType] ?? camelCaseToTitleCase(serviceType)}
            </Typography>
            {!isLoading && availableHours?.services ? (
              <>
                {daysOrder.map((day) => {
                  return (
                    <AvailableHoursSelector
                      key={`${serviceType}-${day}`}
                      day={day}
                      isActive={
                        availableHours.services.find((s) => s.serviceType === serviceType)
                          ?.availability[day]?.isActive
                      }
                      clientTimeZone={timezone}
                      timeRanges={
                        availableHours.services.find((s) => s.serviceType === serviceType)
                          ?.availability[day]?.ranges
                      }
                      newAvailableHours={availableHours}
                      updateDayData={({ isActive, timeRanges }) =>
                        updateDayData({
                          serviceType,
                          day,
                          isActive,
                          timeRanges,
                        })
                      }
                      editMode={editMode}
                      copyToDays={({ selectedDays }) =>
                        copyToDays({ serviceType, day, selectedDays })
                      }
                    />
                  );
                })}
                {editMode && (
                  <Typography
                    variant="caption"
                    sx={{ fontStyle: "italic", marginTop: "16px" }}
                  >{`All times are in the client's time zone (${formatTimeZoneToAbbreviation(
                    timezone
                  )})`}</Typography>
                )}
              </>
            ) : (
              <CircularProgress size={30} color="primary" style={{ marginBottom: 10 }} />
            )}
          </Box>
        ))}
      </Stack>
    </SectionWrapper>
  );
};

export default WeeklyAvailability;
export type { AvailableHours };
